import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  'hoodies-sweaters': 'ri-t-shirt-line',
  't-shirts': 'ri-shirt-line',
  jackets: 'ri-t-shirt-air-line',
  'pants-shorts': 'ri-body-scan-line',
  headwear: 'ri-glasses-line',
  sets: 'ri-stack-line',
  underwear: 'ri-checkbox-blank-circle-line',
  jersey: 'ri-flag-line',
  accessories: 'ri-brush-line',
  bags: 'ri-shopping-bag-line',
  belts: 'ri-link',
  perfumes: 'ri-drop-line',
  'other-stuff': 'ri-more-line',
};

export default function Header() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setIsGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shoppingGuides = [
    { name: 'Ultimate Guide 2026', path: '/basetao-spreadsheet-guide' },
    { name: 'Is Basetao Legit? Review', path: '/basetao-review' },
    { name: 'Shipping Cost Guide', path: '/basetao-shipping-cost' },
    { name: 'Coupon & Discount Guide', path: '/basetao-coupon-guide' },
    { name: 'Warehouse & QC Guide', path: '/basetao-warehouse-guide' },
    { name: 'Complete Buying Guide', path: '/basetao-buying-guide' },
    { name: 'Safety & Security', path: '/basetao-safety-guide' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b-2 border-background-300/80">
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg font-bold text-foreground-950">
              Basetao Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-semibold text-foreground-800 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setIsDropdownOpen(!isDropdownOpen); setIsGuideOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-foreground-800 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl border border-background-300/80 py-2 shadow-lg">
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="w-7 h-7 rounded-md bg-background-200/70 flex items-center justify-center flex-shrink-0">
                          <i className={`${categoryIcons[cat.slug] || 'ri-price-tag-3-line'} text-foreground-600 text-xs`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setIsGuideOpen(!isGuideOpen); setIsDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-foreground-800 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isGuideOpen && (
                <div className="absolute top-full left-0 mt-1 w-60 bg-background-50 rounded-xl border border-background-300/80 py-2 shadow-lg">
                  {shoppingGuides.map((guide) => (
                    <Link
                      key={guide.path}
                      to={guide.path}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                      onClick={() => setIsGuideOpen(false)}
                    >
                      {guide.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-bold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl text-foreground-900 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t-2 border-background-300/60 py-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex flex-col gap-0.5">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-primary-600"></i>
                </span>
                Home
              </Link>

              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-grid-line text-accent-600"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileCatOpen && (
                <div className="mx-4 mb-2 p-3 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-200/70 rounded-lg transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="w-6 h-6 rounded bg-background-300/60 flex items-center justify-center flex-shrink-0">
                          <i className={`${categoryIcons[cat.slug] || 'ri-price-tag-3-line'} text-foreground-500 text-xs`}></i>
                        </span>
                        <span className="truncate">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Shopping Guides</p>
                {shoppingGuides.map((guide) => (
                  <Link
                    key={guide.path}
                    to={guide.path}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 rounded bg-secondary-100 flex items-center justify-center">
                      <i className="ri-article-line text-secondary-600 text-xs"></i>
                    </span>
                    {guide.name}
                  </Link>
                ))}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mx-4 mt-2 px-5 py-3.5 text-sm font-bold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full text-center transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}